import React from 'react';
import { Table, Checkbox, Button, Dropdown } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import './CustomSimpleTable.css';

// StatusPill component
const StatusPill = ({ status }) => (
  <span className={`status-pill ${status.toLowerCase()}`}>
    {status}
  </span>
);

/**
 * CustomSimpleTable - A reusable table component with custom styling
 * 
 * @param {Array} data - Table data
 * @param {Array} columns - Table columns configuration
 * @param {function} onSelect - Callback for row selection
 * @param {Array} selectedRowKeys - Selected row keys
 * @param {boolean} showCheckbox - Whether to show checkboxes
 * @param {boolean} showActions - Whether to show action column
 * @param {function} onEdit - Edit callback
 * @param {function} onDelete - Delete callback
 * @param {function} onToggleStatus - Toggle status callback
 * @param {string} className - Additional CSS classes
 */
const CustomSimpleTable = ({
  data = [],
  columns = [],
  onSelect,
  selectedRowKeys = [],
  showCheckbox = true,
  showActions = true,
  onEdit,
  onView,
  onDelete,
  onToggleStatus,
  className = '',
}) => {
  // Add checkbox column if needed
  const checkboxColumn = {
    title: '',
    key: 'checkbox',
    width: 50,
    render: (_, record) => (
      <Checkbox
        checked={selectedRowKeys.includes(record.key)}
        onChange={(e) => {
          if (onSelect) {
            const newSelectedKeys = e.target.checked
              ? [...selectedRowKeys, record.key]
              : selectedRowKeys.filter(key => key !== record.key);
            onSelect(newSelectedKeys, data.filter(item => newSelectedKeys.includes(item.key)));
          }
        }}
      />
    ),
  };

  // Add actions column if needed
  const actionsColumn = {
    title: '',
    key: 'actions',
    width: 50,
    render: (_, record) => {
      const actionItems = [];
      
      if (onView) {
        actionItems.push({
          key: 'view',
          label: 'View',
          onClick: () => onView(record)
        });
      }
      
      if (onEdit) {
        actionItems.push({
          key: 'edit',
          label: 'Edit',
          onClick: () => onEdit(record)
        });
      }
      
      if (onToggleStatus) {
        actionItems.push({
          key: 'toggle',
          label: record.status ? 'Deactivate' : 'Activate',
          onClick: () => onToggleStatus(record)
        });
      }
      
      if (onDelete) {
        actionItems.push({
          key: 'delete',
          label: 'Delete',
          onClick: () => onDelete(record)
        });
      }

      if (actionItems.length === 0) return null;

      return (
        <Dropdown
          menu={{ items: actionItems }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      );
    },
  };

  // Build final columns
  const finalColumns = [];
  
  if (showCheckbox) {
    finalColumns.push(checkboxColumn);
  }
  
  finalColumns.push(...columns);
  
  if (showActions) {
    finalColumns.push(actionsColumn);
  }

  return (
    <div className={`custom-simple-table ${className}`}>
      <Table
        dataSource={data}
        columns={finalColumns}
        pagination={false}
        rowKey="key"
        className="custom-table"
        showHeader={true}
        size="middle"
      />
    </div>
  );
};

export default CustomSimpleTable;
export { StatusPill };
